import { G } from '@mobily/ts-belt';
import GlobalStyles from '@mui/material/GlobalStyles';
import { TourProvider, useTour } from '@react-admin/ra-tour';
import { useEffect, useState } from 'react';
import {
  Layout as RaLayout,
  LayoutProps as RaLayoutProps,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useLocation, useNavigate } from 'react-router-dom';

import { PutBoExternalTutorialDoneEndpoint } from '@Common/types/bo_external/admins';

import { AppLocationContext } from '@sh4res/bo-common/components';
import { getTheme } from '@sh4res/bo-common/theme';

import { AnalyticsErrorPage } from '@Components/AnalyticsErrorPage';
import { AppBar } from '@Components/AppBar';
import { AppBreadcrumb } from '@Components/Breadcrumb';
import { Footer } from '@Components/Footer';
import { Menu } from '@Components/Layout/Menu';
import { useGetTours } from '@Components/Layout/tours';
import { useSetTutorialDone } from '@Components/Layout/tours/useSetTutorialDone';
import { useInactivityLogout } from '@Hooks/useInactivityLogout';
import { getAuthSession } from '@Utils/authClient';
import { STORAGE_KEYS } from '@Utils/storage_keys';

import PublicoHeadlineBold from '@Assets/fonts/PublicoHeadline-Bold.ttf';

export const Layout = (props: RaLayoutProps) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { data: identity } = useGetIdentity();

  useInactivityLogout();

  const verificationDone = useEnforcePhoneNumber();

  const setTutorialDone = useSetTutorialDone();
  const tours = useGetTours({ identity });

  // to avoid flickering
  if (!verificationDone) {
    return null;
  }

  return (
    <AppLocationContext>
      <GlobalStyles
        styles={{
          body: {
            margin: 0,
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            webkitFontSmoothing: 'antialiased',
            mozOsxFontSmoothing: 'grayscale',
          },
          code: {
            fontFamily: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
          },
          '.react-joyride__overlay': {
            height: '100vh !important',
          },
          '.RaLayout-content': {
            display: 'flex',
            flexDirection: 'column',
          },
          '.show-page': {
            flexGrow: 1,
            height: 'auto !important',
          },
          '.list-page': {
            flexGrow: 1,
          },
          '@media only screen': {
            html: {
              overflow: 'hidden',
              height: '100%',
            },
            body: {
              height: '100%',
              overflow: 'hidden',
            },
          },
          '@font-face': {
            fontFamily: 'PublicoHeadline',
            src: `url(${PublicoHeadlineBold}) format('truetype')`,
            fontStyle: 'normal',
            fontWeight: 700,
            fontDisplay: 'swap',
          },
        }}
      />
      <TourProvider
        tours={tours}
        tools={{
          notify,
          redirect,
          setTutorialDone,
        }}
        joyrideProps={{
          scrollContainerSelector: '.RaLayout-content',
          styles: {
            options: {
              zIndex: 9999,
            },
          },
        }}>
        <>
          <TourStarter userId={identity?.id} />
          <RaLayout {...props} appBar={AppBar} menu={Menu} error={AnalyticsErrorPage} role="main">
            <AppBreadcrumb />
            {props.children}
            {getTheme() === 'axa' ? <Footer /> : null}
          </RaLayout>
        </>
      </TourProvider>
    </AppLocationContext>
  );
};

const TourStarter = ({ userId }: { userId: number | undefined }) => {
  const loginSuccessTourCheck = localStorage.getItem(STORAGE_KEYS.loginSuccessTourCheck) === 'true';
  const [{ running }, { start }] = useTour();
  const location = useLocation();

  const shouldCheckTutorialDone =
    G.isNotNullable(userId) &&
    loginSuccessTourCheck &&
    start &&
    !running &&
    // don't show tutorial if navigating to another page
    !location.state?.nextPathname;

  const { data: tutorialResponse } = useGetOne<
    PutBoExternalTutorialDoneEndpoint['Client']['ResponseBody']
  >(
    'admins/tutorial_done',
    { id: G.isNotNullable(userId) ? userId : 0 },
    {
      enabled: shouldCheckTutorialDone,
    },
  );

  useEffect(() => {
    if (shouldCheckTutorialDone && tutorialResponse && !tutorialResponse.is_tutorial_done) {
      start('welcome-tour');
      localStorage.removeItem(STORAGE_KEYS.loginSuccessTourCheck);
    } else if (tutorialResponse && tutorialResponse.is_tutorial_done) {
      localStorage.removeItem(STORAGE_KEYS.loginSuccessTourCheck);
    }
  }, [shouldCheckTutorialDone, tutorialResponse, start]);

  return null;
};

// check that phone number is actually verified
const useEnforcePhoneNumber = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checkDone, setCheckDone] = useState(false);

  useEffect(() => {
    const func = async () => {
      const { data } = await getAuthSession();

      if (!data) {
        return;
      }

      // ignore phone verification for ghost login or when alternate email is configured for 2FA
      if (
        !data.user.phoneNumberVerified &&
        !data.session.impersonatedBy &&
        !data.user.alternateEmail
      ) {
        // oxlint-disable-next-line no-console
        // eslint-disable-next-line no-console
        console.error('Phone number is not verified');
        navigate('/verify_phone_number', {
          state: {
            nextPathname: location.pathname,
            nextSearch: location.search,
          },
        });
      } else {
        setCheckDone(true);
      }
    };

    func();
    // adding `navigate` as dependency is causing refetching on every page change
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return checkDone;
};
